import { Ligne_Document_Vente } from '../models/models';
import { TypeLigne } from '../constants/enums';

/**
 * Synthèse des coûts d'un document de fabrication.
 *
 * Méthode de calcul :
 *   - Coût matière direct par article = somme des composants liés via composant_de_ligne_id
 *   - Frais (CHARGE) et remises (REMISE) sont ventilés au prorata du coût matière direct
 *     de chaque ligne ARTICLE (méthode des sections homogènes — un article qui consomme
 *     plus de matière porte plus de frais indirects).
 *   - Fallback : si toutes les matières directes sont nulles (article sans nomenclature),
 *     ventilation par quantité.
 *
 * Hypothèses :
 *   - Les lignes COMPOSANT sont injectées avec tva=0 → total_ht === total_ttc.
 *   - Le total_ht/total_ttc des lignes est calculé par la BD (colonnes GENERATED).
 */
export interface CoutFabricationSynthese {
  /** Coût matière directe = somme(total_ht) des lignes COMPOSANT */
  coutMatiereTotal: number;
  /** Somme(total_ht) des lignes CHARGE — peut être négatif selon le sens du document */
  totalCharges: number;
  /** Somme(total_ht) des lignes REMISE — généralement négatif */
  totalRemises: number;
  /** Ajustements à ventiler = totalCharges + totalRemises */
  totalAjustements: number;
  /** Coût total estimé du document = matière + ajustements */
  coutTotalDocument: number;
  /** Valeur de fabrication = somme(total_ht) des lignes ARTICLE (la valeur "de sortie") */
  valeurFabriqueeHT: number;
  /** Marge brute estimée HT = valeurFabriqueeHT − coutTotalDocument */
  margeEstimeeHT: number;
  /** Pourcentage de marge sur la valeur fabriquée (0 si valeur nulle) */
  margePct: number;
  /**
   * Détail par ligne ARTICLE — accessible via l'id de la ligne.
   * Les lignes non-ARTICLE ne figurent pas dans la map.
   */
  parLigne: Map<number, CoutLigneArticle>;
}

export interface CoutLigneArticle {
  ligneId: number;
  quantite: number;
  /** Coût matière direct (somme des composants liés à cette ligne) */
  coutMatiereDirect: number;
  /** Part de frais + remises allouée à cette ligne (peut être négative) */
  partAjustement: number;
  /** Coût complet = matière directe + part ajustement */
  coutComplet: number;
  /** Coût unitaire = coutComplet / quantité (0 si quantité = 0) */
  coutUnitaire: number;
}

/**
 * Calcule la synthèse des coûts pour un document de fabrication.
 *
 * Cette fonction reste pure : elle n'opère que sur les lignes passées en argument
 * et ne dépend d'aucune source externe. Sûre à appeler dans un useMemo.
 */
export function computeCoutFabrication(
  lignes: Ligne_Document_Vente[],
): CoutFabricationSynthese {
  const lignesArticle   = lignes.filter(l => (l.type_ligne ?? TypeLigne.ARTICLE) === TypeLigne.ARTICLE);
  const lignesComposant = lignes.filter(l => l.type_ligne === TypeLigne.COMPOSANT);
  const lignesCharge    = lignes.filter(l => l.type_ligne === TypeLigne.CHARGE);
  const lignesRemise    = lignes.filter(l => l.type_ligne === TypeLigne.REMISE);

  const sumHT = (arr: Ligne_Document_Vente[]) => arr.reduce((s, l) => s + (l.total_ht ?? 0), 0);

  const coutMatiereTotal = sumHT(lignesComposant);
  const totalCharges     = sumHT(lignesCharge);
  const totalRemises     = sumHT(lignesRemise);
  const totalAjustements = totalCharges + totalRemises;
  const valeurFabriqueeHT = sumHT(lignesArticle);

  // Coût matière direct par ligne ARTICLE — somme des composants pointant vers elle.
  const matiereDirectParLigne = new Map<number, number>();
  for (const c of lignesComposant) {
    const parentId = c.composant_de_ligne_id;
    if (parentId == null) continue;
    matiereDirectParLigne.set(parentId, (matiereDirectParLigne.get(parentId) ?? 0) + (c.total_ht ?? 0));
  }

  // Base de ventilation : matière directe, avec fallback quantité.
  const baseMatiere = coutMatiereTotal;
  const baseQte     = lignesArticle.reduce((s, l) => s + (l.quantite ?? 0), 0);

  const partAjustementPour = (ligne: Ligne_Document_Vente): number => {
    if (totalAjustements === 0) return 0;
    if (baseMatiere > 0) {
      const matiereDirect = matiereDirectParLigne.get(ligne.id ?? -1) ?? 0;
      return totalAjustements * (matiereDirect / baseMatiere);
    }
    if (baseQte > 0) {
      return totalAjustements * ((ligne.quantite ?? 0) / baseQte);
    }
    return 0;
  };

  const parLigne = new Map<number, CoutLigneArticle>();
  for (const a of lignesArticle) {
    if (a.id == null) continue;
    const coutMatiereDirect = matiereDirectParLigne.get(a.id) ?? 0;
    const partAjustement    = partAjustementPour(a);
    const coutComplet       = coutMatiereDirect + partAjustement;
    const quantite          = a.quantite ?? 0;
    parLigne.set(a.id, {
      ligneId: a.id,
      quantite,
      coutMatiereDirect,
      partAjustement,
      coutComplet,
      coutUnitaire: quantite > 0 ? coutComplet / quantite : 0,
    });
  }

  const coutTotalDocument = coutMatiereTotal + totalAjustements;
  const margeEstimeeHT    = valeurFabriqueeHT - coutTotalDocument;
  const margePct          = valeurFabriqueeHT > 0 ? (margeEstimeeHT / valeurFabriqueeHT) * 100 : 0;

  return {
    coutMatiereTotal,
    totalCharges,
    totalRemises,
    totalAjustements,
    coutTotalDocument,
    valeurFabriqueeHT,
    margeEstimeeHT,
    margePct,
    parLigne,
  };
}
